import { FilePurpose } from '@/api/enums';
import { isKeycloakEnabled } from '@/auth';
import { client } from '@/client';
import {
  InteractiveElement,
  InteractiveElementType,
  SlideTemplateType,
} from '@/client/courses/types/courses.type';
import { File } from '@/client/files';
import {
  CONTENT_GIF_MAX_SIZE,
  CONTENT_IMAGE_ACCEPTED_FORMATS,
  CONTENT_VIDEO_ACCEPTED_FORMATS,
  CONTENT_VIDEO_MAX_SIZE,
} from '@/common/constants/files';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import {
  canUploadContentVideo,
  getContentMediaMaxSize,
  isVideoFile,
} from '@/utils/helpers/content-media.helper';
import {
  ContentMediaUploadDefaultIcon,
  ContentMediaUploadEmailIcon,
  ContentMediaUploadSMSIcon,
} from '@/utils/helpers/icons.helper';
import {
  FileUpload,
  FileUploadProps,
  FileUploadSelectEvent,
  FileUploadUploadEvent,
} from 'primereact/fileupload';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

const StyledFlexContainer = styled(FlexContainer)`
  position: relative;
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  margin-top: 6px;

  &.white {
    background: var(--white-main);
  }
`;

const StyledUploadWrapper = styled(FlexContainer)`
  flex: 1 1 45%;
  background: var(--beige-main);
  border-radius: 4px;
  min-width: 200px;
  min-height: 90px;
  padding: var(--small-padding);
  word-break: break-word;
  position: relative;

  .icon {
    color: var(--gray-darker);
    font-size: var(--body-font-size);
  }
`;

const StyledThumbnail = styled(FlexContainer)`
  overflow: hidden;
  border-radius: 4px;

  img,
  video {
    max-width: 100px;
    max-height: 100px;
    width: auto;
    height: auto;
  }
`;

const StyledInteractiveElementIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: var(--beige-light);
  margin: auto;

  i {
    margin: auto;
    font-size: 2rem;
    color: var(--gray-darker);
  }
`;

const StyledError = styled.p`
  color: var(--red-main);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: 0;
  width: 100%;
`;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
`;

const HiddenFileUpload = styled.div`
  display: none;
`;

const StyledRemoveButton = styled(AppButton)`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledDropdownButton = styled(AppButton)`
  width: 180px !important;
`;

const StyledUploadingOverlay = styled(FlexContainer)`
  position: absolute;
  inset: 0;
  z-index: 1001;
  background: var(--white-main);
  border-radius: var(--xsmall-border-radius);

  .p-progress-spinner {
    width: 32px;
    height: 32px;
  }
`;

type ContentMediaUploadProps = {
  label?: string;
  tooltip?: string;
  icon?: React.ReactNode;
  preselectedFile?: File | null;
  preselectedInteractiveElement?: InteractiveElement | null;
  onFileUpload: (file: File | null) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
  isDisabled?: boolean;
  required?: boolean;
  hasError?: boolean;
  additionalImgOptions?: React.ReactNode;
  itemId?: string;
  templateType?: SlideTemplateType;
  purpose?: FilePurpose;
  onInteractiveElementAdd?: (
    itemId: string,
    interactiveElement: InteractiveElement | undefined,
  ) => void;
  onInteractiveElementUpdate?: (
    interactiveElement: InteractiveElement | undefined,
  ) => void;
};

export const ContentMediaUpload: React.FC<ContentMediaUploadProps> = ({
  label,
  tooltip,
  icon,
  preselectedFile,
  preselectedInteractiveElement,
  onFileUpload,
  setIsSubmitDisabled,
  isDisabled,
  hasError,
  additionalImgOptions,
  itemId,
  templateType,
  purpose,
  onInteractiveElementAdd,
  onInteractiveElementUpdate,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const currentUser = useAppSelector(selectCurrentUser);
  const canUploadVideo = canUploadContentVideo(currentUser);
  const isKeycloakAuth = isKeycloakEnabled();
  const [error, setError] = useState<string[]>([]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedInteractiveElement, setSelectedInteractiveElement] =
    useState<InteractiveElement | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const fileUploadRef = useRef<FileUpload>(null);
  const videoUploadRef = useRef<FileUpload>(null);
  const menuRef = useRef<Menu>(null);

  const validImageTypes = CONTENT_IMAGE_ACCEPTED_FORMATS.map(
    (format) => `image/${format.slice(1)}`,
  );
  const validVideoTypes = CONTENT_VIDEO_ACCEPTED_FORMATS.map(
    (format) => `video/${format.slice(1)}`,
  );

  useEffect(() => {
    setSelectedFile(preselectedFile || null);
  }, [preselectedFile]);

  useEffect(() => {
    setSelectedInteractiveElement(preselectedInteractiveElement || null);
  }, [preselectedInteractiveElement]);

  const parseUploadResponse = (raw?: string) => {
    try {
      return JSON.parse(raw ?? '');
    } catch {
      return null;
    }
  };

  const onUpload = async (e: FileUploadUploadEvent) => {
    const response = parseUploadResponse(e.xhr.response);

    if (!response) {
      setError((state) => [t('error.fileUploadFailed'), ...state]);
      changeUploadingState(false);

      return;
    }

    try {
      setSelectedFile(response);
      setError([]);
      onFileUpload(response);
    } finally {
      changeUploadingState(false);
    }
  };

  const clearUpload = () => {
    setSelectedFile(null);
    onFileUpload(null);
    setError([]);
  };

  const clearInteractiveElement = () => {
    setSelectedInteractiveElement(null);
    if (itemId && onInteractiveElementAdd) {
      onInteractiveElementAdd(itemId, undefined);
    } else if (onInteractiveElementUpdate) {
      onInteractiveElementUpdate(undefined);
    }
  };

  const changeUploadingState = (isUploading: boolean) => {
    setIsUploading(isUploading);
    setIsSubmitDisabled(isUploading);
  };

  const handleUploadImage = () => {
    fileUploadRef.current?.getInput()?.click();
  };

  const handleUploadVideo = () => {
    videoUploadRef.current?.getInput()?.click();
  };

  const validateSelectedFile =
    (validTypes: string[]) =>
    ({ originalEvent }: FileUploadSelectEvent) => {
      const input =
        'dataTransfer' in originalEvent ? null : originalEvent.target;
      const file = input
        ? input.files?.[0]
        : (originalEvent as DragEvent).dataTransfer?.files?.[0];

      if (!file) return true;

      const rejectFile = (message: string) => {
        setError((state) => [message, ...state]);
        if (input) input.value = '';
        return false;
      };

      if (!validTypes.includes(file.type)) {
        return rejectFile(
          t('error.invalidFileType', { types: validTypes.join(', ') }),
        );
      }

      const maxSize = getContentMediaMaxSize(file.type);

      if (file.size > maxSize) {
        return rejectFile(t('error.fileSize', { mb: maxSize / (1000 * 1000) }));
      }

      return true;
    };

  const handleAddSMS = () => {
    setSelectedFile(null);
    onFileUpload(null);
    const newElement = {
      type: InteractiveElementType.SMS,
      data: {},
    };
    setSelectedInteractiveElement(newElement);
    if (itemId && onInteractiveElementAdd) {
      onInteractiveElementAdd(itemId, newElement);
    } else if (onInteractiveElementUpdate) {
      onInteractiveElementUpdate(newElement);
    }
  };

  const handleAddEmail = () => {
    setSelectedFile(null);
    onFileUpload(null);
    const newElement = {
      type: InteractiveElementType.EMAIL,
      data: {},
    };
    setSelectedInteractiveElement(newElement);
    if (itemId && onInteractiveElementAdd) {
      onInteractiveElementAdd(itemId, newElement);
    } else if (onInteractiveElementUpdate) {
      onInteractiveElementUpdate(newElement);
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: t('generic.upload.image'),
      command: handleUploadImage,
    },
    ...(canUploadVideo
      ? [
          {
            label: t('generic.upload.video'),
            command: handleUploadVideo,
          },
        ]
      : []),
    // Only show SMS and Email options if not gallery type
    ...(templateType !== SlideTemplateType.GALLERY
      ? [
          {
            label: t('content.sms.add'),
            command: handleAddSMS,
          },
          {
            label: t('content.email.add'),
            command: handleAddEmail,
          },
        ]
      : []),
  ];

  const toggleMenu = (event: SyntheticEvent<HTMLButtonElement>) => {
    menuRef.current?.toggle(event);
  };

  const getButtonLabel = () => {
    if (selectedInteractiveElement) {
      return selectedInteractiveElement.type === InteractiveElementType.EMAIL
        ? t('content.email.add')
        : t('content.sms.add');
    }
    return t('content.media.add');
  };

  const sharedUploadProps: FileUploadProps = {
    mode: 'basic',
    name: 'file',
    url: client.files.getFileUploadUrl(),
    onUpload,
    auto: true,
    withCredentials: true,
    onError: (event) => {
      const response = parseUploadResponse(event?.xhr?.response);
      const message = response?.message || t('error.fileUploadFailed');

      setError((state) => (state.length > 0 ? state : [message]));
      changeUploadingState(false);
    },
    onBeforeSend: (event) => {
      if (user && isKeycloakAuth) {
        event.xhr.setRequestHeader(
          'Authorization',
          `Bearer ${user?.access_token}`,
        );
      }
    },
    onBeforeUpload: (event) => {
      setError([]);
      changeUploadingState(true);
      event.formData.append('purpose', purpose || FilePurpose.MATERIAL);
    },
  };

  return (
    <div>
      <StyledLabel className={hasError ? 'p-error' : undefined}>
        {label && (
          <>
            {label} {tooltip ? <span className="red ml-1"> *</span> : ''}
          </>
        )}
        {tooltip && (
          <>
            <i className="pi pi-info-circle ml-2" />
            <Tooltip
              target=".pi-info-circle"
              content={tooltip}
              my="center bottom-20"
            />
          </>
        )}
      </StyledLabel>
      <StyledFlexContainer
        gap={24}
        justify="space-between"
        wrap="wrap"
        className="white"
      >
        <StyledUploadWrapper className="flex-1">
          {selectedFile?.id && !isUploading && (
            <FlexContainer justify="space-between">
              <StyledThumbnail flex="1">
                {isVideoFile(selectedFile) ? (
                  <video
                    src={selectedFile.signedUrl}
                    preload="metadata"
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    src={selectedFile?.signedUrl}
                    alt={selectedFile?.fileName}
                  />
                )}
              </StyledThumbnail>
              {!isDisabled && (
                <StyledRemoveButton
                  type="text"
                  severity="secondary"
                  icon="pi pi-times"
                  onClick={clearUpload}
                />
              )}
            </FlexContainer>
          )}
          {selectedInteractiveElement && !selectedFile?.id && !isUploading && (
            <FlexContainer justify="space-between">
              <StyledInteractiveElementIcon>
                {selectedInteractiveElement.type === 'email' ? (
                  <ContentMediaUploadEmailIcon />
                ) : (
                  <ContentMediaUploadSMSIcon />
                )}
              </StyledInteractiveElementIcon>
              {!isDisabled && (
                <StyledRemoveButton
                  type="text"
                  severity="secondary"
                  icon="pi pi-times"
                  onClick={clearInteractiveElement}
                />
              )}
            </FlexContainer>
          )}
          {!selectedFile?.id &&
            !selectedInteractiveElement &&
            !isUploading &&
            (icon ? icon : <ContentMediaUploadDefaultIcon />)}
        </StyledUploadWrapper>
        {!isDisabled && (
          <FlexContainer
            direction="column"
            align="flex-start"
            gap={12}
            flex="0 0 max-content"
          >
            <Menu
              model={menuItems}
              popup
              ref={menuRef}
              id="slide-upload-menu"
              style={{ width: 'auto', minWidth: '196px' }}
            />
            <StyledDropdownButton
              type="outlined"
              label={getButtonLabel()}
              icon="pi pi-angle-down"
              iconPos="right"
              aria-controls="slide-upload-menu"
              onClick={toggleMenu}
            />
            <HiddenFileUpload>
              <FileUpload
                {...sharedUploadProps}
                ref={fileUploadRef}
                accept={CONTENT_IMAGE_ACCEPTED_FORMATS.join(',')}
                maxFileSize={CONTENT_GIF_MAX_SIZE}
                onBeforeSelect={validateSelectedFile(validImageTypes)}
              />
              {canUploadVideo && (
                <FileUpload
                  {...sharedUploadProps}
                  ref={videoUploadRef}
                  accept={CONTENT_VIDEO_ACCEPTED_FORMATS.join(',')}
                  maxFileSize={CONTENT_VIDEO_MAX_SIZE}
                  onBeforeSelect={validateSelectedFile(validVideoTypes)}
                />
              )}
            </HiddenFileUpload>
            {additionalImgOptions}
          </FlexContainer>
        )}
        {isUploading && (
          <StyledUploadingOverlay justify="center" align="center">
            <ProgressSpinner />
          </StyledUploadingOverlay>
        )}
        {!!error?.length && (
          <StyledError>
            {error.map((error, index) => (
              <span key={index}>
                {error}.<br />
              </span>
            ))}
          </StyledError>
        )}
      </StyledFlexContainer>
    </div>
  );
};
